import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import type { CalendarEventItem } from "@/types/task";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Video,
  MapPin,
  Users,
  Send,
  CheckCircle2,
  Trash2,
  ExternalLink,
  RotateCcw,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";
import { dateLocale, withClock } from "@/lib/dateTime";

interface TaskDetailSheetProps {
  event: CalendarEventItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}

export function TaskDetailSheet({
  event,
  open,
  onOpenChange,
  onUpdated,
  onDeleted,
}: TaskDetailSheetProps) {
  const confirm = useConfirm();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!event) return null;

  const isExternal = event.kind === "EXTERNAL";
  const start = new Date(event.start);
  const end = new Date(event.end);

  const formattedDate = start.toLocaleDateString(dateLocale(), {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const formattedTime = event.all_day
    ? "All Day Event"
    : `${start.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }))} – ${end.toLocaleTimeString(dateLocale(), withClock({
        hour: "2-digit",
        minute: "2-digit" }))}`;

  const handleResendInvite = async () => {
    setActionLoading("invite");
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await api.post(`/api/Task/Invite/${event.id}`, {});
      unwrapObject(res);
      setSuccessMsg("Meeting invitation (.ics) successfully dispatched!");
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to send meeting invite");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleComplete = async () => {
    setActionLoading("complete");
    setErrorMsg(null);
    try {
      if (event.task_status === "DONE") {
        await api.patch(`/api/Task/Reopen/${event.id}`, {});
      } else {
        await api.patch(`/api/Task/Complete/${event.id}`, {});
      }
      onUpdated?.();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to update task status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ title: `Delete "${event.title}"?`, message: "This can't be undone.", confirmLabel: "Delete", destructive: true }))) return;
    setActionLoading("delete");
    try {
      await api.delete(`/api/Task/Delete/${event.id}`);
      onDeleted?.();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to delete task");
    } finally {
      setActionLoading(null);
    }
  };

  const getEntityUrl = () => {
    if (!event.related_type || !event.related_id) return null;
    switch (event.related_type) {
      case "LEAD":
        return `/leads/${event.related_id}`;
      case "DEAL":
        return `/deals/${event.related_id}`;
      case "ACCOUNT":
        return `/accounts/${event.related_id}`;
      case "CONTACT":
        return `/contacts/${event.related_id}`;
      case "QUOTE":
        return `/quotes/${event.related_id}`;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`text-[10px] font-bold tracking-wider ${
                isExternal
                  ? "bg-accent text-accent-foreground border-primary/20 "
                  : event.task_type === "MEETING"
                  ? "bg-primary/10 text-primary border-primary/20 "
                  : "bg-primary/10 text-primary border-primary/20"
              }`}
            >
              {isExternal ? `${event.provider} CALENDAR` : event.task_type || "TASK"}
            </Badge>

            {event.priority && (
              <Badge
                variant="outline"
                className={`text-[10px] font-semibold ${
                  event.priority === "HIGH"
                    ? "bg-danger-soft text-danger border-danger/20"
                    : event.priority === "LOW"
                    ? "bg-muted text-foreground border-border"
                    : "bg-info-soft text-info border-info/20"
                }`}
              >
                {event.priority}
              </Badge>
            )}

            {event.task_status === "DONE" && (
              <Badge
                variant="outline"
                className="text-[10px] bg-success-soft text-success border-success/20"
              >
                DONE
              </Badge>
            )}
          </div>

          <DialogTitle className="text-lg font-bold mt-2 leading-snug">
            {event.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {formattedDate} &bull; {formattedTime}
          </DialogDescription>
        </DialogHeader>

        {successMsg && (
          <div className="p-3 rounded-lg bg-success-soft text-success text-xs border border-success/20 flex items-center gap-2">
            <CheckCircle2 className="size-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="p-3 rounded-lg bg-danger-soft text-danger text-xs border border-danger/20 flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="space-y-3.5 py-1 text-xs">
          {/* Video meeting link */}
          {event.online_link && (
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Video className="size-4" />
                </div>
                <div>
                  <div className="font-semibold text-xs text-foreground">
                    {event.meeting_provider ? event.meeting_provider.replace("_", " ") : "Video Meeting"}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">
                    {event.online_link}
                  </div>
                </div>
              </div>
              <Button size="sm" asChild className="text-xs h-8">
                <a href={event.online_link} target="_blank" rel="noreferrer">
                  Join Meeting
                </a>
              </Button>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="size-3.5 text-foreground shrink-0" />
              <span className="text-foreground">{event.location}</span>
            </div>
          )}

          {/* Linked entity */}
          {event.related_name && (
            <div className="p-2.5 rounded-lg border border-border bg-card/60 flex items-center justify-between">
              <span className="text-muted-foreground text-[11px]">
                Linked {event.related_type?.toLowerCase()}:
              </span>
              {getEntityUrl() ? (
                <Link
                  to={getEntityUrl()!}
                  className="font-semibold text-primary hover:underline flex items-center gap-1"
                >
                  {event.related_name}
                  <ExternalLink className="size-3" />
                </Link>
              ) : (
                <span className="font-semibold text-foreground">{event.related_name}</span>
              )}
            </div>
          )}

          {/* Assignee */}
          {event.assigned_to && (
            <div className="text-muted-foreground text-[11px] flex items-center gap-1.5">
              <span>Owner:</span>
              <span className="font-semibold text-foreground">
                {[event.assigned_to.first_name, event.assigned_to.last_name]
                  .filter(Boolean)
                  .join(" ") || event.assigned_to.email}
              </span>
            </div>
          )}

          {/* Attendees */}
          {event.attendees && event.attendees.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <div className="font-semibold text-xs text-foreground flex items-center gap-1.5">
                <Users className="size-3.5 text-muted-foreground" />
                <span>Attendees ({event.attendees.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {event.attendees.map((att, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded-md bg-muted text-[11px] font-medium border border-border text-foreground"
                  >
                    {att.name || att.email}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="space-y-1 pt-1">
              <div className="font-semibold text-xs text-foreground">Notes & Agenda</div>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed bg-muted/30 p-2.5 rounded-lg border border-border/60">
                {event.description}
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-3 border-t border-border mt-1">
          {!isExternal ? (
            <>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={actionLoading === "delete"}
                  className="text-xs text-danger hover:text-danger hover:bg-danger-soft"
                >
                  <Trash2 className="size-3.5 mr-1" /> Delete
                </Button>

                {event.task_type === "MEETING" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendInvite}
                    disabled={actionLoading === "invite"}
                    className="text-xs"
                    title="Resend RFC 5545 invite email (.ics)"
                  >
                    {actionLoading === "invite" ? (
                      <Loader2 className="size-3.5 animate-spin mr-1" />
                    ) : (
                      <Send className="size-3.5 mr-1" />
                    )}
                    Send Invite
                  </Button>
                )}
              </div>

              <Button
                size="sm"
                onClick={handleToggleComplete}
                disabled={actionLoading === "complete"}
                className={`text-xs ${
                  event.task_status === "DONE"
                    ? "bg-muted text-muted-foreground hover:bg-muted/80"
                    : "bg-success hover:bg-success/90 text-white"
                }`}
              >
                {event.task_status === "DONE" ? (
                  <>
                    <RotateCcw className="size-3.5 mr-1" /> Reopen
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-3.5 mr-1" /> Mark Done
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] text-muted-foreground">
                Synced from external provider (read-only)
              </span>
              {event.provider_url && (
                <Button size="sm" variant="outline" asChild className="text-xs">
                  <a href={event.provider_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-3.5 mr-1" /> Open in {event.provider}
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
